/* eslint-disable @typescript-eslint/no-use-before-define */
import * as React from 'react';
import { useMemo } from 'react';
import { motion } from 'framer-motion';
import { graphql, useStaticQuery } from 'gatsby';
import styled, { css } from 'styled-components';

import { SEO } from '@/components/global/SEO';
import Page from '@/components/v2/Page';
import { V1GridLines, V1PageWrapper } from '@/components/v2/Page/V1PageWrapper';
import { SOCIAL_SHARE } from '@/utils/social-share-images';

import { CustomerGridItem } from '../../components/customers/CustomerGridItem';
import { Heading } from '../../components/design-system';
import { ANIMATIONS, INTERNAL_LINKS } from '../../utils';

const Wrapper = styled(motion.div)`
  padding: 0 40px;
  width: 100%;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  margin: 0 auto;
  margin-bottom: 200px;
`;

const Header = styled.div`
  margin-bottom: 60px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.SMALL}) {
    margin-bottom: 40px;
  }

  h1 {
    margin-bottom: 24px;
    position: relative;
    text-indent: -5px;

    &::after {
      content: '';
      display: block;
      position: absolute;
      left: 0;
      width: 135px;
      bottom: -4px;
      height: 1px;
      background-color: ${({ theme }) => theme.borderColor.DEFAULT};
    }
  }

  p {
    max-width: 700px;
  }
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Grid = styled.div<{ $isStories?: boolean }>`
  flex: 1 1 auto;
  display: flex;
  max-width: none;
  flex-wrap: wrap;
  margin: -10px;
  width: auto;
  padding-top: 0;

  ${({ $isStories }) =>
    $isStories &&
    css`
      margin-bottom: 50px;
      justify-content: center;
    `}
`;

interface CustomerStory {
  synopsis: {
    synopsis: string;
  };
}

export interface CustomerItem {
  customer_story: CustomerStory[] | null;
  logo: {
    url: string;
  };
  name: string;
  slug: string;
  website: string;
}

export interface CustomerLogoItem {
  name: string;
  publicURL: string;
}

interface CustomerPageData {
  allContentfulCustomer: {
    nodes: CustomerItem[];
  };
}

const CustomerPage: React.ComponentType = React.memo(() => {
  const data: CustomerPageData = useStaticQuery(pageQuery);

  const logoGrid = useMemo(() => {
    const allItems = [...data.allContentfulCustomer.nodes];
    const caseStudies = allItems.filter(o => o.customer_story !== null);
    const noCaseStudies = allItems.filter(o => o.customer_story == null);
    return (
      <List>
        <Grid $isStories={true}>
          {caseStudies.map((item, index) => (
            <CustomerGridItem
              key={index}
              isStory={true}
              item={item}
              loudStyle={true}
            />
          ))}
        </Grid>
        <Grid>
          {noCaseStudies.map((item, index) => (
            <CustomerGridItem key={index} item={item} />
          ))}
          <CustomerGridItem
            isManyMore={true}
            isStory={false}
            item={{
              customer_story: null,
              logo: {
                url: ''
              },
              slug: '',
              name: '',
              website: ''
            }}
          />
        </Grid>
      </List>
    );
  }, [data.allContentfulCustomer.nodes]);

  return (
    <Page
      seo={
        <SEO
          description="Join the world's leading data teams using Hex to do more, together."
          image={SOCIAL_SHARE.Customers}
          title="Customers"
          url={INTERNAL_LINKS.Customers}
        />
      }>
      <V1GridLines>
        <V1PageWrapper>
          <Wrapper {...ANIMATIONS.SimpleFadeIn}>
            <Header>
              <Heading renderAs="h1">Customers</Heading>
              <p>
                Join the world's leading data teams using Hex to do more,
                together.
              </p>
            </Header>
            {logoGrid}
          </Wrapper>
        </V1PageWrapper>
      </V1GridLines>
    </Page>
  );
});

const pageQuery = graphql`
  query {
    allContentfulCustomer(
      sort: { slug: ASC }
      filter: { omitFromCustomersPage: { ne: true } }
    ) {
      nodes {
        slug
        name
        customer_story {
          synopsis {
            synopsis
          }
        }
        logo {
          url
        }
        website
      }
    }
  }
`;

export default CustomerPage;
